import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { useAuth } from "./AuthContext";
import { useSocket } from "./SocketContext";
import {
  getNotifications,
  markNotificationRead as markNotificationReadApi,
  markAllNotificationsRead as markAllNotificationsReadApi,
} from "../api/notifications";

const NotificationContext = createContext(null);

function useNotificationState() {
  const { isAuthenticated, user } = useAuth();
  const { socket } = useSocket();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Signing in as someone else (even without logging out) drops the last account's
  // notifications at once, and a reply still on its way for that account is ignored.
  const accountId = user?.id || user?._id || null;
  const accountRef = useRef(accountId);
  accountRef.current = accountId;
  const [shownFor, setShownFor] = useState(accountId);
  if (shownFor !== accountId) {
    setShownFor(accountId);
    setNotifications([]);
    setUnreadCount(0);
  }

  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    try {
      const res = await getNotifications(50);
      if (accountRef.current !== accountId) return;
      if (res.success && res.data) {
        setNotifications(res.data.notifications || []);
        setUnreadCount(
          typeof res.data.unreadCount === "number" ? res.data.unreadCount : 0
        );
      }
    } catch (e) {
      console.error("fetchNotifications:", e);
    }
  }, [isAuthenticated, accountId]);

  useEffect(() => {
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 60000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  useEffect(() => {
    if (!socket || !isAuthenticated) return;

    const onNew = (payload) => {
      const n = payload?.notification;
      if (!n || !n._id) return;
      setNotifications((prev) => {
        const id = String(n._id);
        if (prev.some((x) => String(x._id) === id)) return prev;
        return [n, ...prev];
      });
      if (!n.read) {
        setUnreadCount((c) => c + 1);
      }
    };

    // Read elsewhere — the chat they're about was opened, here or on another device.
    const onRead = (payload) => {
      const ids = new Set((payload?.ids || []).map(String));
      if (!ids.size) return;
      setNotifications((prev) => prev.map((n) => (ids.has(String(n._id)) ? { ...n, read: true } : n)));
      fetchNotifications();
    };

    socket.on("notification_new", onNew);
    socket.on("notifications_read", onRead);
    return () => {
      socket.off("notification_new", onNew);
      socket.off("notifications_read", onRead);
    };
  }, [socket, isAuthenticated, fetchNotifications]);

  const markRead = useCallback(
    async (id) => {
      try {
        const res = await markNotificationReadApi(id);
        if (!res.success || !res.data) return;
        const sid = String(id);
        setNotifications((prev) =>
          prev.map((n) =>
            String(n._id) === sid ? { ...n, read: true } : n
          )
        );
        if (typeof res.data.unreadCount === "number") {
          setUnreadCount(res.data.unreadCount);
        }
      } catch (e) {
        fetchNotifications();
      }
    },
    [fetchNotifications]
  );

  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await markAllNotificationsReadApi();
    } catch (e) {
      fetchNotifications();
    }
  }, [fetchNotifications]);

  return {
    notifications,
    unreadCount,
    fetchNotifications,
    markRead,
    markAllRead,
  };
}

export function NotificationProvider({ children }) {
  const value = useNotificationState();
  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error(
      "useNotifications must be used within NotificationProvider"
    );
  }
  return ctx;
}
